import { subject } from "@casl/ability";
import { createContextualCan } from "@casl/react";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";

import { useUser } from "@/modules/auth/hooks/use-user";

import { AppAbility, defineAbilitiesFor } from "./abilities";

const defaultAbility = defineAbilitiesFor();

const AbilityContext = createContext<AppAbility>(defaultAbility);

const Can = createContextualCan(AbilityContext.Consumer);

const useAbility = () => useContext(AbilityContext);

interface AbilityProviderProps {
  children?: ReactNode;
}

const AbilityProvider = ({ children }: AbilityProviderProps) => {
  const { user } = useUser();
  // Rebuild the CASL ability only when the user (and thus its abilities
  // payload) changes — `user` is reference-stable from React Query. Without
  // this, every render hands a new ability object as the context value and
  // re-renders every <Can> / useBusinessAbilities consumer in the tree.
  const ability = useMemo(() => defineAbilitiesFor(user), [user]);

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};

const useBusinessAbilities = () => {
  const ability = useAbility();

  const canViewProcedures = ability.can("read", "Procedure");
  const canCreateProcedure = ability.can("create", "Procedure");
  const canViewReports = ability.can("read", "Report");
  const canCreateReport = ability.can("create", "Report");
  const canViewSignatures = ability.can("read", "SignatureRequest");
  const canCreateSignature = ability.can("create", "SignatureRequest");

  return {
    canViewProcedures,
    canCreateProcedure,
    canAccessProcedures: canViewProcedures || canCreateProcedure,
    canViewReports,
    canCreateReport,
    canAccessReports: canViewReports || canCreateReport,
    canViewSignatures,
    canCreateSignature,
    canAccessSignatures: canViewSignatures || canCreateSignature,
  };
};

// Whether the user may manage (e.g. send to gestoría) a procedure. The rule is
// scoped per customer, so pass the procedure's `customerId` to evaluate the
// per-customer condition; omit it to check the unconditional grant.
const useCanManageProcedure = (customerId?: string | null) => {
  const ability = useAbility();

  return ability.can(
    "manage",
    subject("Procedure", customerId ? { customerId } : {}),
  );
};

// Returns a stable predicate that answers "may the user edit a signature for
// this customer?". Mirrors the API's `update` gate on
// `PUT /signature-requests/:id`, so `signature:view` (read) cannot edit while
// `signature:full` (manage) can.
//
// With a `customerId` it does a per-customer instance check. Without one it
// falls back to the bare *string* subject, NOT `subject("SignatureRequest", {})`:
// real grants are conditioned on `{ customerId }`, and matching those conditions
// against an empty object always fails — so a full-access user would wrongly
// read as "cannot update". The bare string is CASL's union-across-customers
// check (same shape as the `create` gate).
//
// Prefer passing the `customerId` wherever a row/entity carries it (e.g. the
// signatures list, the edit screen): the union fallback can't tell customers
// apart and would show an edit affordance on rows the user may only view.
const useCanUpdateSignatureFn = () => {
  const ability = useAbility();

  return useCallback(
    (customerId?: string | null) =>
      ability.can(
        "update",
        customerId
          ? subject("SignatureRequest", { customerId })
          : "SignatureRequest",
      ),
    [ability],
  );
};

const useCanUpdateSignature = (customerId?: string | null) =>
  useCanUpdateSignatureFn()(customerId);

// Returns a stable predicate that answers "may the user create a signature for
// this customer?". Mirrors the API's `create` gate on
// `POST /signature-requests/:id/duplicate`, so `signature:view` (read) cannot
// duplicate while `signature:full` (manage) can. Same per-customer / bare
// string fallback reasoning as `useCanUpdateSignatureFn` above.
const useCanCreateSignatureFn = () => {
  const ability = useAbility();

  return useCallback(
    (customerId?: string | null) =>
      ability.can(
        "create",
        customerId
          ? subject("SignatureRequest", { customerId })
          : "SignatureRequest",
      ),
    [ability],
  );
};

export {
  Can,
  useAbility,
  AbilityProvider,
  type AppAbility,
  defineAbilitiesFor,
  AbilityContext,
  useBusinessAbilities,
  useCanManageProcedure,
  useCanCreateSignatureFn,
  useCanUpdateSignature,
  useCanUpdateSignatureFn,
};
